// Überlappungsprüfung für Eltern-Buchungen (Spieler bereits eingeplant).

import { parseSessionStartMs, sessionEndMs } from "./session-timing.js";
import { normalizeSessionPersonIdList } from "./session-rsvp.js";

const DEFAULT_CANCELLED = Object.freeze([
  "cancelled_weather",
  "cancelled_trainer",
  "cancelled_court",
  "cancelled_participants",
  "cancelled_club",
  "cancelled_holiday",
  "postponed",
  "cancelled_other",
]);

function __nz(v) {
  return String(v ?? "").trim();
}

function isCancelledSession(session, cancelledStatuses) {
  const st = __nz(session?.status).toLowerCase() || "planned";
  return cancelledStatuses.includes(st);
}

function sessionIntervalMs(session) {
  const start = parseSessionStartMs(session);
  const end = sessionEndMs(session);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
}

/**
 * @param {{
 *   sessions: object[],
 *   personId: string,
 *   dateYmd: string,
 *   startTime: string,
 *   durationMin?: number,
 *   excludeSessionId?: string,
 *   cancelledStatuses?: string[],
 *   nowMs?: number,
 * }} input
 * @returns {object|null} überlappende Session oder null
 */
export function findPersonSessionBookingOverlap(input) {
  const personId = __nz(input?.personId);
  const dateYmd = __nz(input?.dateYmd).slice(0, 10);
  if (!personId || !dateYmd) return null;

  const cancelled = Array.isArray(input?.cancelledStatuses)
    ? input.cancelledStatuses
    : DEFAULT_CANCELLED;
  const excludeId = __nz(input?.excludeSessionId);
  const nowMs = Number.isFinite(input?.nowMs) ? input.nowMs : Date.now();

  const candStart = parseSessionStartMs({
    date: dateYmd,
    start_time: input?.startTime,
  });
  const candEnd = candStart + (Number(input?.durationMin) || 60) * 60 * 1000;
  if (!Number.isFinite(candStart)) return null;

  for (const session of input?.sessions || []) {
    const sid = __nz(session?.id);
    if (!sid || sid === excludeId) continue;
    if (isCancelledSession(session, cancelled)) continue;
    if (__nz(session?.deleted_at)) continue;
    if (__nz(session?.parent_booking_status) === "rejected") continue;
    if (__nz(session?.date).slice(0, 10) !== dateYmd) continue;

    const planned = normalizeSessionPersonIdList(session?.planned_ids);
    if (!planned.includes(personId)) continue;

    const interval = sessionIntervalMs(session);
    if (!interval) continue;
    if (interval.end <= nowMs && session?.attendance_confirmed) continue;

    if (candStart < interval.end && interval.start < candEnd) {
      return session;
    }
  }

  return null;
}

/** @type {number} */
export const PARENT_BOOKING_MISSED_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * @param {object|null|undefined} session
 */
export function isParentBookingPendingTrainer(session) {
  return __nz(session?.parent_booking_status) === "pending_trainer";
}

/**
 * Ende der 24h-Kulanz für eine verpasste Chance (ms seit Epoch).
 * @param {object|null|undefined} session
 * @returns {number|null}
 */
export function resolveParentBookingMissedGraceDeadlineMs(session) {
  const st = __nz(session?.parent_booking_status);
  if (st === "missed_chance") {
    const anchor = session?.parent_booking_missed_at
      ? new Date(session.parent_booking_missed_at).getTime()
      : sessionEndMs(session);
    if (!Number.isFinite(anchor)) return null;
    return anchor + PARENT_BOOKING_MISSED_GRACE_MS;
  }
  if (st === "pending_trainer" || st === "pending_student") {
    const end = sessionEndMs(session);
    if (!Number.isFinite(end)) return null;
    return end + PARENT_BOOKING_MISSED_GRACE_MS;
  }
  return null;
}

/**
 * Kürzeste Restzeit (ms) unter den sichtbaren verpassten Chancen.
 * @param {object[]|null|undefined} sessions
 * @param {number} [nowMs]
 * @returns {number|null}
 */
export function resolveEarliestParentBookingMissedRemainingMs(
  sessions,
  nowMs = Date.now(),
) {
  let min = Infinity;
  for (const session of Array.isArray(sessions) ? sessions : []) {
    if (!isParentBookingMissedChance(session, nowMs)) continue;
    const deadline = resolveParentBookingMissedGraceDeadlineMs(session);
    if (!Number.isFinite(deadline)) continue;
    const remaining = deadline - nowMs;
    if (remaining > 0 && remaining < min) min = remaining;
  }
  return Number.isFinite(min) ? min : null;
}

/**
 * i18n für Hub-Untertitel „Noch … zur Bearbeitung“.
 * @param {number|null|undefined} remainingMs
 * @returns {{ key: string, params: { hours?: number } }}
 */
export function resolveParentBookingMissedDetailI18n(remainingMs) {
  const base = "trainerstunden.hub.cockpit.tasks.parentBookingMissedDetail";
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    return { key: `${base}.underOneHour`, params: {} };
  }
  if (remainingMs < 60 * 60 * 1000) {
    return { key: `${base}.underOneHour`, params: {} };
  }
  const hours = Math.max(1, Math.ceil(remainingMs / (60 * 60 * 1000)));
  if (hours === 1) {
    return { key: `${base}.one`, params: { hours: 1 } };
  }
  return { key: `${base}.other`, params: { hours } };
}

/**
 * @param {object|null|undefined} session
 * @param {number} [nowMs]
 */
export function isParentBookingMissedChance(session, nowMs = Date.now()) {
  const st = __nz(session?.parent_booking_status);
  if (st === "missed_chance") {
    const deadline = resolveParentBookingMissedGraceDeadlineMs(session);
    if (!Number.isFinite(deadline)) return false;
    return nowMs < deadline;
  }
  if (st === "pending_trainer" || st === "pending_student") {
    const end = sessionEndMs(session);
    if (!Number.isFinite(end) || end > nowMs) return false;
    const deadline = resolveParentBookingMissedGraceDeadlineMs(session);
    if (!Number.isFinite(deadline)) return false;
    return nowMs < deadline;
  }
  return false;
}

/**
 * Trainer hat Gegenvorschlag gesendet — wartet auf Antwort des Spielers.
 * Abgelaufene Slots sind keine offenen Anfragen mehr.
 * @param {object|null|undefined} session
 * @param {number} [nowMs]
 */
export function isParentBookingAwaitingPlayerResponse(
  session,
  nowMs = Date.now(),
) {
  if (__nz(session?.parent_booking_status) !== "pending_student") return false;
  if (isCancelledSession(session, DEFAULT_CANCELLED)) return false;
  const end = sessionEndMs(session);
  return !Number.isFinite(end) || end > nowMs;
}

/**
 * Offene Anfrage (Termin noch nicht vorbei).
 * @param {object|null|undefined} session
 * @param {number} [nowMs]
 */
export function isParentBookingPendingTrainerReview(
  session,
  nowMs = Date.now(),
) {
  if (!isParentBookingPendingTrainer(session)) return false;
  if (isCancelledSession(session, DEFAULT_CANCELLED)) return false;
  const end = sessionEndMs(session);
  return !Number.isFinite(end) || end > nowMs;
}

/**
 * Trainer soll Anfrage oder verpasste Chance bearbeiten.
 * @param {object|null|undefined} session
 * @param {number} [nowMs]
 */
export function isParentBookingAwaitingTrainerReview(
  session,
  nowMs = Date.now(),
) {
  if (isCancelledSession(session, DEFAULT_CANCELLED)) return false;
  return (
    isParentBookingPendingTrainerReview(session, nowMs) ||
    isParentBookingMissedChance(session, nowMs)
  );
}

/**
 * @param {object[]|null|undefined} sessions
 * @param {{ now?: Date }} [opts]
 */
export function listParentBookingPendingTrainerSessions(sessions, opts = {}) {
  const now = opts.now instanceof Date ? opts.now : new Date();
  const nowMs = now.getTime();
  return (Array.isArray(sessions) ? sessions : []).filter((s) =>
    isParentBookingPendingTrainerReview(s, nowMs),
  );
}

/**
 * @param {object[]|null|undefined} sessions
 * @param {{ now?: Date }} [opts]
 */
export function listParentBookingMissedChanceSessions(sessions, opts = {}) {
  const now = opts.now instanceof Date ? opts.now : new Date();
  const nowMs = now.getTime();
  return (Array.isArray(sessions) ? sessions : []).filter((s) =>
    isParentBookingMissedChance(s, nowMs),
  );
}

/**
 * @param {object[]|null|undefined} sessions
 * @param {{ now?: Date }} [opts]
 */
export function listParentBookingAwaitingPlayerResponseSessions(
  sessions,
  opts = {},
) {
  const now = opts.now instanceof Date ? opts.now : new Date();
  const nowMs = now.getTime();
  return (Array.isArray(sessions) ? sessions : []).filter((s) =>
    isParentBookingAwaitingPlayerResponse(s, nowMs),
  );
}

function dedupeSessions(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const id = __nz(row?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}

/**
 * @param {object[]|null|undefined} sessions
 * @param {{ now?: Date }} [opts]
 * @returns {{ pending: object[], missed: object[], awaitingPlayer: object[], open: object[], all: object[] }}
 */
export function listParentBookingTrainerAttentionSessions(sessions, opts = {}) {
  const pending = listParentBookingPendingTrainerSessions(sessions, opts);
  const missed = listParentBookingMissedChanceSessions(sessions, opts);
  const awaitingPlayer = listParentBookingAwaitingPlayerResponseSessions(
    sessions,
    opts,
  );
  const open = dedupeSessions([...pending, ...awaitingPlayer]);
  const all = dedupeSessions([...open, ...missed]);
  return { pending, missed, awaitingPlayer, open, all };
}
